import {
  type FdbAttribution,
  type FdbScreeningOverride,
  type PrescriptionChangeLink,
  type PrescriptionRenewalLink,
  type PrintablePrescriptionItemState,
  PrintablePrescriptionItemStateSchema,
  type StructuredSig,
} from "@emr/contracts";
import {
  type PatientProfile,
  type PatientWorkspace,
  type PrintablePrescriptionDetails,
  type PrintablePrescriptionItem,
  PrintablePrescriptionItemSchema,
  type PrintablePrescriptionItemsDetails,
  type PrintablePrescriptionRecord,
  type PrintablePrescriptionRequest,
  PrintablePrescriptionRequestSchema,
  patientHealthIdentifier,
} from "../api/clinicalWorkspace";
import {
  type PrescriptionPadContent,
  type PrescriptionPadMedication,
  type PrescriptionPadPatientIdentifier,
  type PrescriptionSignatureImage,
  renderPrescriptionPad,
} from "./prescriptionPdfTemplate";

/**
 * Client-side assembly for api.create_and_sign_printable_prescription.
 *
 * The RPC stores the exact payload the physician signed, so the browser must
 * supply the rendered PDF, its sha256 document hash, and the sha256 content
 * hash of the canonical prescription json. Nothing here contacts an eRx
 * partner; the output is a printable pad only.
 *
 * A prescription lists one medication (the flat form) or several in items[]
 * (RX-3), signed once as one document.
 */

export interface PrintablePrescriptionDraft {
  readonly medicationDisplay: string;
  readonly sig: string;
  readonly quantity: number;
  readonly quantityUnit: string;
  readonly refills: number;
  readonly indication: string;
  readonly substitutionAllowed: boolean;
  readonly daysSupply?: number | undefined;
  /** FDB MEDID picked from MedKnowledge search; absent for free text. */
  readonly medicationCode?: string | undefined;
  readonly structuredSig?: StructuredSig | undefined;
  /** Override reasons for the screening alerts that require one. */
  readonly overrides?: readonly FdbScreeningOverride[] | undefined;
  /** FDB attribution to print on the pad when the medication is FDB-coded. */
  readonly attribution?: FdbAttribution | null | undefined;
  /** A refill (EXECDEV-101): the signed prescription item this medication renews. */
  readonly renewalOf?: PrescriptionRenewalLink | undefined;
  /** A change (EXECDEV-176): the signed prescription item this medication replaces. */
  readonly changeOf?: PrescriptionChangeLink | undefined;
  /**
   * A change: what the pad says it replaces, so the pharmacy knows the earlier
   * prescription no longer stands. Printed only; the signed item carries changeOf.
   */
  readonly replaces?: { readonly medicationDisplay: string; readonly signedAt: string } | undefined;
}

export interface PrintablePrescriptionPatient {
  readonly patientId: string;
  readonly displayName: string;
  readonly dateOfBirth: string;
  readonly mrn?: string;
  /** Formatted postal address when the chart records one; printed on the pad only. */
  readonly address?: string | undefined;
  /**
   * The coverage member ID with its label — "Health Card #" for a
   * provincial/federal plan, "Member ID" for private insurance. Printed on the
   * pad only; never part of the signed RPC payload.
   */
  readonly healthIdentifier?: PrescriptionPadPatientIdentifier | undefined;
}

/**
 * The pad's patient shape from a loaded workspace: the chart identity the RPC
 * payload carries plus the pad-only address and health identifier.
 */
export function printablePatientFromWorkspace(
  workspace: Pick<
    PatientWorkspace,
    "id" | "displayName" | "dob" | "mrn" | "address" | "healthCardNumber" | "coverageDetails"
  >,
): PrintablePrescriptionPatient {
  const address = workspace.address?.trim();
  const healthIdentifier = patientHealthIdentifier(workspace);
  return {
    patientId: workspace.id,
    displayName: workspace.displayName,
    dateOfBirth: workspace.dob,
    mrn: workspace.mrn,
    ...(address ? { address } : {}),
    ...(healthIdentifier === undefined ? {} : { healthIdentifier }),
  };
}

/**
 * The pad's patient shape from the durable profile, for a prescription signed
 * outside a visit (EXECDEV-101): the name, address and health identifier the
 * encounter workspace prints for the same chart.
 */
export function printablePatientFromProfile(
  profile: Pick<PatientProfile, "patientId" | "demographics">,
): PrintablePrescriptionPatient {
  const { demographics } = profile;
  const address = [
    demographics.address?.line1,
    demographics.address?.line2,
    demographics.address?.city,
    demographics.address?.region ?? demographics.address?.province ?? demographics.address?.state,
    demographics.address?.postalCode,
    demographics.address?.country,
  ]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0)
    .join(", ");
  const coverage = demographics.coverage;
  const healthIdentifier = patientHealthIdentifier({
    healthCardNumber: demographics.healthNumber,
    coverageDetails:
      coverage === undefined
        ? undefined
        : {
            memberId: coverage.memberId,
            payerName: coverage.payerName,
            payerKind: coverage.payerKind,
            status: coverage.status,
          },
  });
  return {
    patientId: profile.patientId,
    displayName: `${demographics.preferredName?.trim() || demographics.legalFirstName} ${demographics.legalLastName}`,
    dateOfBirth: demographics.dateOfBirth,
    mrn: demographics.mrn,
    ...(address ? { address } : {}),
    ...(healthIdentifier === undefined ? {} : { healthIdentifier }),
  };
}

export interface PrintablePrescriptionPrescriber {
  readonly signatureName: string;
  readonly licenseNumber: string;
  readonly licenseJurisdiction: string;
  readonly organizationName?: string | undefined;
  readonly siteName?: string | undefined;
  readonly siteAddress?: string | undefined;
  readonly sitePhone?: string | undefined;
  readonly siteFax?: string | undefined;
  /** IANA zone the pad's dates are printed in; UTC when absent or invalid. */
  readonly siteTimezone?: string | undefined;
}

/** Deterministic json with recursively sorted object keys. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    const entries = Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export async function sha256OfBytes(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes.slice().buffer);
  return `sha256:${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export async function sha256OfText(text: string): Promise<string> {
  return sha256OfBytes(new TextEncoder().encode(text));
}

export function base64FromBytes(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

/**
 * Decodes a stored base64 PDF (whitespace tolerated — persisted records may
 * wrap the payload) into raw bytes.
 */
export function pdfBytesFromBase64(documentBase64: string): Uint8Array {
  const binary = atob(documentBase64.replaceAll(/\s+/g, ""));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

/**
 * Decodes a stored base64 PDF into a Blob the browser's PDF viewer can
 * display or print.
 */
export function pdfBlobFromBase64(documentBase64: string): Blob {
  return new Blob([pdfBytesFromBase64(documentBase64).buffer as ArrayBuffer], {
    type: "application/pdf",
  });
}

export function prescriptionVerificationLabel(record: PrintablePrescriptionRecord): string {
  return record.verification === "verified" ? "Verified" : "Could not verify";
}

/** Only server-verified signed bytes may enter a viewer or a print package. */
export function verifiedPrescriptionPdfBytes(record: PrintablePrescriptionRecord): Uint8Array {
  if (record.verification !== "verified" || record.documentBase64.length === 0) {
    throw new Error("Could not verify the signed prescription PDF.");
  }
  return pdfBytesFromBase64(record.documentBase64);
}

const PAD_LOCALE = "en-US";

/** ICU pads times with narrow no-break spaces, which WinAnsi cannot print. */
function plainSpaces(value: string): string {
  return value.replaceAll(/[  ]/g, " ");
}

function safeTimeZone(timeZone: string | undefined): string {
  if (timeZone === undefined || timeZone.trim() === "") return "UTC";
  try {
    new Intl.DateTimeFormat(PAD_LOCALE, { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
}

/** "1982-01-14" → "Jan 14, 1982"; anything unparseable prints as typed. */
export function formatDateOfBirth(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match === null) return value.trim();
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return value.trim();
  return plainSpaces(
    new Intl.DateTimeFormat(PAD_LOCALE, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date),
  );
}

function formatSignedDate(signedAtIso: string, timeZone: string): string {
  const date = new Date(signedAtIso);
  if (Number.isNaN(date.getTime())) return signedAtIso;
  return plainSpaces(
    new Intl.DateTimeFormat(PAD_LOCALE, { dateStyle: "long", timeZone }).format(date),
  );
}

function formatSignedAt(signedAtIso: string, timeZone: string): string {
  const date = new Date(signedAtIso);
  if (Number.isNaN(date.getTime())) return signedAtIso;
  return plainSpaces(
    new Intl.DateTimeFormat(PAD_LOCALE, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      timeZone,
    }).format(date),
  );
}

function padMedication(
  draft: PrintablePrescriptionDraft,
  timeZone: string,
): PrescriptionPadMedication {
  return {
    medication: draft.medicationDisplay,
    sig: draft.sig,
    dispense: `${draft.quantity} ${draft.quantityUnit}`,
    refills: draft.refills === 0 ? "None" : String(draft.refills),
    details: [
      `Indication: ${draft.indication}`,
      `Substitution ${draft.substitutionAllowed ? "permitted" : "not permitted"}`,
      ...(draft.daysSupply === undefined ? [] : [`Days supply: ${draft.daysSupply}`]),
      ...(draft.replaces === undefined
        ? []
        : [
            `Replaces the prescription of ${formatSignedDate(draft.replaces.signedAt, timeZone)} for ${draft.replaces.medicationDisplay.trim()}`,
          ]),
    ],
  };
}

/** Pure content model for the pad; the renderer only lays it out. */
export function prescriptionPadContent(args: {
  readonly patient: PrintablePrescriptionPatient;
  readonly prescriber: PrintablePrescriptionPrescriber;
  /** Every medication on the prescription, in order. */
  readonly drafts: readonly PrintablePrescriptionDraft[];
  readonly signedAtIso: string;
}): PrescriptionPadContent {
  const { patient, prescriber, drafts, signedAtIso } = args;
  const timeZone = safeTimeZone(prescriber.siteTimezone);
  const clinicName = prescriber.siteName?.trim() || prescriber.organizationName?.trim() || "";
  const licenseLine = `Licence ${prescriber.licenseNumber} · ${prescriber.licenseJurisdiction}`;
  return {
    clinic: {
      name: clinicName === "" ? "Prescription" : clinicName,
      address: prescriber.siteAddress,
      phone: prescriber.sitePhone,
      fax: prescriber.siteFax,
    },
    prescriber: { name: prescriber.signatureName, licenseLine },
    patient: {
      name: patient.displayName,
      dateOfBirth: formatDateOfBirth(patient.dateOfBirth),
      mrn: patient.mrn,
      address: patient.address,
      identifier: patient.healthIdentifier,
    },
    date: formatSignedDate(signedAtIso, timeZone),
    medications: drafts.map((draft) => padMedication(draft, timeZone)),
    signedAt: `Signed electronically ${formatSignedAt(signedAtIso, timeZone)}`,
    // The attribution is the same for every FDB-coded medication; it prints once.
    sourceNotice: drafts.map(fdbSourceNotice).find((notice) => notice !== undefined),
  };
}

/** FDB and Health Canada attribution, required wherever FDB content prints. */
export function fdbSourceNotice(draft: PrintablePrescriptionDraft): string[] | undefined {
  if (draft.medicationCode === undefined || !draft.attribution) return undefined;
  return [
    `Drug information: ${draft.attribution.disclaimer} ${draft.attribution.copyright}`,
    ...(draft.attribution.drugProductDatabase === null
      ? []
      : [draft.attribution.drugProductDatabase]),
  ];
}

/** The signed fields of one medication: trimmed, coded when FDB-coded. */
export function prescriptionItemFromDraft(
  draft: PrintablePrescriptionDraft,
): PrintablePrescriptionItem {
  return {
    medicationDisplay: draft.medicationDisplay.trim(),
    sig: draft.sig.trim(),
    quantity: draft.quantity,
    quantityUnit: draft.quantityUnit.trim(),
    refills: draft.refills,
    indication: draft.indication.trim(),
    substitutionAllowed: draft.substitutionAllowed,
    ...(draft.daysSupply === undefined ? {} : { daysSupply: draft.daysSupply }),
    ...(draft.medicationCode === undefined
      ? {}
      : { medicationCode: draft.medicationCode, medicationCodeSystem: "FDB_MEDID" as const }),
    ...(draft.structuredSig === undefined ? {} : { structuredSig: draft.structuredSig }),
    ...(draft.renewalOf === undefined ? {} : { renewalOf: draft.renewalOf }),
    ...(draft.changeOf === undefined ? {} : { changeOf: draft.changeOf }),
  };
}

interface PrintablePrescriptionBuildArgs {
  readonly siteId: string;
  readonly idempotencyKey: string;
  readonly patient: PrintablePrescriptionPatient;
  readonly prescriber: PrintablePrescriptionPrescriber;
  readonly signedAtIso?: string;
  /** Enrolled provider signature; the pad falls back to a typed block without it. */
  readonly signatureImage?: PrescriptionSignatureImage | undefined;
}

/** Renders, hashes and assembles the request around a prescription payload. */
async function assemblePrintablePrescriptionRequest(
  args: PrintablePrescriptionBuildArgs & {
    readonly drafts: readonly PrintablePrescriptionDraft[];
    readonly prescription: PrintablePrescriptionDetails | PrintablePrescriptionItemsDetails;
    readonly overrides: Pick<
      PrintablePrescriptionRequest["safetyEvaluation"],
      "overrides" | "items"
    >;
    readonly summary: string;
  },
): Promise<PrintablePrescriptionRequest> {
  const signedAtIso = args.signedAtIso ?? new Date().toISOString();
  const pdfBytes = await renderPrescriptionPad(
    prescriptionPadContent({
      patient: args.patient,
      prescriber: args.prescriber,
      drafts: args.drafts.map((draft) => ({ ...draft, ...prescriptionItemFromDraft(draft) })),
      signedAtIso,
    }),
    { signedAtIso, signatureImage: args.signatureImage },
  );
  const [contentHash, documentHash] = await Promise.all([
    sha256OfText(canonicalJson(args.prescription)),
    sha256OfBytes(pdfBytes),
  ]);
  return PrintablePrescriptionRequestSchema.parse({
    siteId: args.siteId,
    idempotencyKey: args.idempotencyKey,
    patient: {
      patientId: args.patient.patientId,
      displayName: args.patient.displayName,
      dateOfBirth: args.patient.dateOfBirth,
      ...(args.patient.mrn === undefined ? {} : { mrn: args.patient.mrn }),
    },
    prescription: args.prescription,
    safetyEvaluation: {
      valid: true,
      evaluatedAt: signedAtIso,
      summary: args.summary,
      ...args.overrides,
    },
    contentHash,
    documentHash,
    documentBase64: base64FromBytes(pdfBytes),
    fileName: `prescription-${args.idempotencyKey.slice(0, 12)}.pdf`,
  });
}

/** The flat single-medication request. */
export async function buildPrintablePrescriptionRequest(
  args: PrintablePrescriptionBuildArgs & { readonly draft: PrintablePrescriptionDraft },
): Promise<PrintablePrescriptionRequest> {
  return assemblePrintablePrescriptionRequest({
    ...args,
    drafts: [args.draft],
    prescription: { operation: "new", source: "manual", ...prescriptionItemFromDraft(args.draft) },
    overrides:
      args.draft.overrides === undefined || args.draft.overrides.length === 0
        ? {}
        : { overrides: [...args.draft.overrides] },
    summary:
      "Physician reviewed the exact medication, dose, quantity, refills, and indication before signing this print-only prescription.",
  });
}

/**
 * One prescription listing every medication in items[] (RX-3): one document,
 * one content hash, one signature. Each medication's override reasons travel
 * at its own index of safetyEvaluation.items. A refill (EXECDEV-101) signs
 * with operation "renew", a change (EXECDEV-176) with "change".
 */
export async function buildPrintablePrescriptionItemsRequest(
  args: PrintablePrescriptionBuildArgs & {
    readonly drafts: readonly PrintablePrescriptionDraft[];
    readonly operation?: "new" | "renew" | "change";
  },
): Promise<PrintablePrescriptionRequest> {
  if (args.drafts.length === 0) {
    throw new Error("A prescription lists at least one medication.");
  }
  const overridden = args.drafts.some((draft) => (draft.overrides?.length ?? 0) > 0);
  return assemblePrintablePrescriptionRequest({
    ...args,
    prescription: {
      operation: args.operation ?? "new",
      source: "manual",
      items: args.drafts.map(prescriptionItemFromDraft),
    },
    overrides: overridden
      ? {
          items: args.drafts.map((draft) =>
            draft.overrides === undefined || draft.overrides.length === 0
              ? {}
              : { overrides: [...draft.overrides] },
          ),
        }
      : {},
    summary:
      args.drafts.length === 1
        ? "Physician reviewed the exact medication, dose, quantity, refills, and indication before signing this print-only prescription."
        : "Physician reviewed the exact medications, doses, quantities, refills, and indications before signing this print-only prescription.",
  });
}

/**
 * The medications a signed prescription lists, in order: every entry of an
 * items[] prescription (RX-3), or the one a flat prescription carries. Entries
 * that do not parse are left out.
 */
export function prescriptionItemsOf(prescription: unknown): PrintablePrescriptionItem[] {
  return prescriptionEntries(prescription).flatMap((candidate) => {
    const parsed = PrintablePrescriptionItemSchema.safeParse(candidate);
    return parsed.success ? [parsed.data] : [];
  });
}

function prescriptionEntries(prescription: unknown): readonly unknown[] {
  if (typeof prescription !== "object" || prescription === null) return [];
  const items = (prescription as { readonly items?: unknown }).items;
  return Array.isArray(items) ? items : [prescription];
}

/**
 * Each medication's name at its item index, trimmed; undefined where a name is
 * missing. A flat record lists its one medication at index 0.
 */
export function prescriptionMedicationNames(
  record: PrintablePrescriptionRecord,
): (string | undefined)[] {
  return prescriptionEntries(record.prescription).map((entry) => {
    const display =
      typeof entry === "object" && entry !== null
        ? (entry as { readonly medicationDisplay?: unknown }).medicationDisplay
        : undefined;
    return typeof display === "string" && display.trim().length > 0 ? display.trim() : undefined;
  });
}

/** The medication names a signed record lists, trimmed, in order. */
export function prescriptionMedicationDisplays(record: PrintablePrescriptionRecord): string[] {
  return prescriptionMedicationNames(record).filter((name): name is string => name !== undefined);
}

/** Whether a signed record renews earlier prescriptions: a refill (EXECDEV-101). */
export function isRenewalRecord(record: PrintablePrescriptionRecord): boolean {
  const prescription = record.prescription;
  return (
    typeof prescription === "object" &&
    prescription !== null &&
    (prescription as { readonly operation?: unknown }).operation === "renew"
  );
}

/** "Amoxicillin 500 mg", "A and B", "A, B and C": what a signed record prescribes. */
export function prescriptionTitle(record: PrintablePrescriptionRecord): string | undefined {
  const displays = prescriptionMedicationDisplays(record);
  if (displays.length <= 1) return displays[0];
  return `${displays.slice(0, -1).join(", ")} and ${displays.at(-1)}`;
}

/** Whether a signed record changes earlier prescriptions (EXECDEV-176). */
export function isChangeRecord(record: PrintablePrescriptionRecord): boolean {
  const prescription = record.prescription;
  return (
    typeof prescription === "object" &&
    prescription !== null &&
    (prescription as { readonly operation?: unknown }).operation === "change"
  );
}

/**
 * What became of each medication of a listed record since it was signed
 * (EXECDEV-176), by item index. A record signed in this session, or listed by
 * an older server, carries none: every medication then reads as active.
 */
export function prescriptionItemStates(
  record: PrintablePrescriptionRecord,
): readonly PrintablePrescriptionItemState[] {
  if (!Array.isArray(record.itemStates)) return [];
  return record.itemStates.flatMap((candidate) => {
    const parsed = PrintablePrescriptionItemStateSchema.safeParse(candidate);
    return parsed.success ? [parsed.data] : [];
  });
}

/** The state of one medication; active when nothing says otherwise. */
export function prescriptionItemState(
  states: readonly PrintablePrescriptionItemState[],
  itemIndex: number,
): PrintablePrescriptionItemState | undefined {
  return states.find((state) => state.itemIndex === itemIndex);
}

/**
 * Whether a record may still be printed or given to the patient: none of its
 * medications was cancelled or changed. A signed PDF cannot be edited, so a
 * prescription listing a cancelled medication is for the record only.
 */
export function prescriptionStillPrintable(
  states: readonly PrintablePrescriptionItemState[],
): boolean {
  return states.every((state) => state.status === "active");
}
